import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Content,
  ContextualHelp,
  Heading as HeadingV3,
  Text
} from '@adobe/react-spectrum';

import Alert from '@react/react-spectrum/Alert';
import Button from '@react/react-spectrum/Button';
import Heading from '@react/react-spectrum/Heading';
import IllustratedMessage from '@react/react-spectrum/IllustratedMessage';
import Wait from '@react/react-spectrum/Wait';

import ACCCard from '../../fragments/cards/ACCCard';
import ACCSidebar from '../../fragments/sidebars/ACCSidebar';
import ACSCard from '../../fragments/cards/ACSCard';
import ACSSidebar from '../../fragments/sidebars/ACSSidebar';
import AEPCard from '../../fragments/cards/AEPCard';
import AEPSidebar from '../../fragments/sidebars/AEPSidebar';
import EmptyBox from '../../assets/illustrations/EmptyBox';
import MarketoCard from '../../fragments/cards/MarketoCard';
import MarketoSidebar from '../../fragments/sidebars/MarketoSidebar';
import TargetCard from '../../fragments/cards/TargetCard';
import TargetSidebar from '../../fragments/sidebars/TargetSidebar';

import { RootContext, TYPES } from '../../root/RootContext';
import { useMyIMSOrgs } from '../../data/hooks';
import { useSidebar } from '../App';
import { IMS_ORG_EXTENSIONS } from '../../config/extensions';
import { PATHS } from 'config/constants';

export default function ImsOrgs() {
  const history = useHistory();
  const { dispatch } = useContext(RootContext);
  const { loading, error, data } = useMyIMSOrgs();
  const { show: showSidebar, hide: closeSidebar } = useSidebar();

  if (loading) {
    return (
      <div className="wait-wrapper">
        <Wait size="S" /> Loading IMS solutions...
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-wrapper">
        <Alert variant="error" header="Error">
          {error.message}
        </Alert>
      </div>
    );
  }

  const { myIMSOrgs } = data;

  const setActiveAEP = (aep) => (e) => {
    e.stopPropagation();
    dispatch({ type: TYPES.AEP_SET_ACTIVE, aep });
    showSidebar(<AEPSidebar />);
  };

  const setActiveTarget = (imsOrg) => (e) => {
    e.stopPropagation();
    dispatch({
      type: TYPES.TARGET_SET_ACTIVE,
      target: {
        imsOrg,
        clientCode: imsOrg.targetClientCode
      }
    });
    showSidebar(<TargetSidebar />);
  };

  const setActiveACS = (acs) => (e) => {
    e.stopPropagation();
    dispatch({
      type: TYPES.ACS_SET_ACTIVE,
      acs
    });
    showSidebar(<ACSSidebar />);
  };

  const setActiveACC = (acc) => (e) => {
    e.stopPropagation();
    dispatch({
      type: TYPES.ACC_SET_ACTIVE,
      acc
    });
    showSidebar(<ACCSidebar />);
  };

  const setActiveSidebarItem = (Sidebar, item) => (e) => {
    e.stopPropagation();
    dispatch({ type: TYPES.GENERIC_SET_ACTIVE, activeSidebarItem: item });
    showSidebar(Sidebar);
  };

  if (Array.isArray(myIMSOrgs) && myIMSOrgs.length > 0) {
    return myIMSOrgs.map((imsOrg) => {
      const admins = listAdmins(imsOrg, 3);
      return (
        <div key={`aep-${imsOrg.id}`}>
          <div style={{ display: 'flex', alignItems: 'end', width: '100%' }}>
            <Heading style={{ flexGrow: 1 }} variant="pageTitle">
              {imsOrg.name}
            </Heading>
            {imsOrg.role === 'admin' && (
              <Button
                style={{ flexGrow: 0 }}
                onClick={() => history.push(PATHS.AEP_ADMIN_CONFIGURE)}>
                Manage users
              </Button>
            )}
          </div>
          <small className="organization-id">
            {imsOrg.id}
            {admins && (
              <>
                {' '}
                | <AdminList admins={admins} />
              </>
            )}
          </small>

          <div>
            {imsOrg.aep.map((aep) => (
              <span
                onClick={setActiveAEP(aep)}
                key={`sandbox-${imsOrg}-${aep.sandboxName}`}>
                <AEPCard data={aep} />
              </span>
            ))}

            {imsOrg.targetClientCode && (
              <span
                onClick={setActiveTarget(imsOrg)}
                key={`target-${imsOrg.targetClientCode}`}>
                <TargetCard imsOrg={imsOrg} />
              </span>
            )}

            {imsOrg.acs.map((acs) => (
              <span
                onClick={setActiveACS(acs)}
                key={`acs-${imsOrg}-${acs.url}`}>
                <ACSCard data={acs} />
              </span>
            ))}

            {imsOrg.acc.map((acc) => (
              <span
                onClick={setActiveACC(acc)}
                key={`acc-${imsOrg}-${acc.url}`}>
                <ACCCard data={acc} />
              </span>
            ))}

            {imsOrg.marketo?.map((marketo) => (
              <span
                onClick={setActiveSidebarItem(
                  <MarketoSidebar
                    instance={marketo}
                    openPanel={(i) =>
                      history.push(PATHS.MARKETO_CONFIGURE, { i })
                    }
                  />,
                  marketo
                )}
                key={`marketo-${imsOrg}-${marketo.url}`}>
                <MarketoCard data={marketo} />
              </span>
            ))}

            {IMS_ORG_EXTENSIONS.map((ext) =>
              ext.renderItems(
                imsOrg,
                history,
                setActiveSidebarItem,
                closeSidebar
              )
            )}
          </div>
        </div>
      );
    });
  } else {
    return (
      <div className="empty-placeholder">
        <IllustratedMessage
          heading="No results"
          description="Looks like you don't have any environments available. Please either register your instance or request a new one."
          illustration={<EmptyBox />}
        />
        <div className="actions">
          <Button
            variant="primary"
            onClick={() => window.open('mailto:demosystem@adobe.com')}>
            Register instance
          </Button>
          <Button
            variant="cta"
            onClick={() =>
              window.open(
                'https://salessupport.adobe.com/content/forms/af/offerings/accounts/new/demo-hosting.html',
                '_blank'
              )
            }>
            Request demo environment
          </Button>
        </div>
      </div>
    );
  }
}

function AdminList({ admins }) {
  return (
    <>
      <b>Need access?</b>
      <ContextualHelp variant="info">
        <HeadingV3>Need help?</HeadingV3>
        <Content>
          <Text>
            <p>
              If you need access to a sandbox that you don't see here, try
              contacting:
            </p>
            <ul>
              {admins.map((admin) => (
                <li key={admin}>{admin}</li>
              ))}
            </ul>{' '}
            <p>
              or let us know on{' '}
              <a
                href="https://sv-core-tech.slack.com/archives/CPCRVPLDQ"
                target="_blank"
                rel="noreferrer">
                #demo-system-next
              </a>{' '}
              Slack channel.
            </p>
          </Text>
        </Content>
      </ContextualHelp>
    </>
  );
}

function listAdmins(imsOrg, limit) {
  if (Array.isArray(imsOrg?.users)) {
    const admins = imsOrg.users
      .filter((u) => u.role === 'admin')
      .map((u) => u.userID)
      .slice(0, limit);
    return admins.length > 0 ? admins : null;
  }
}
